import React, { useState, useEffect, useCallback, useContext, memo } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Grid,
  CircularProgress,
  Alert,
  Paper,
  Snackbar,
  MenuItem,
  Select,
  IconButton,
  InputLabel,
  FormControl,
  Switch,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import DeleteIcon from '@mui/icons-material/Delete';

// Styled components
const SectionTitle = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(2),
  fontWeight: 600,
  color: '#1e293b',
  fontSize: '1.6rem',
  lineHeight: 1.3,
  position: 'relative',
  '&:before': {
    content: '""',
    position: 'absolute',
    left: -16,
    top: 0,
    height: '100%',
    width: 4,
    background: 'linear-gradient(to bottom, #14b8a6, #0d9488)',
  },
  fontFamily: "'Inter', sans-serif",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    borderRadius: 12,
    background: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(15, 23, 42, 0.1)',
    transition: 'all 0.3s ease',
    fontFamily: "'Inter', sans-serif",
    '&:hover': {
      borderColor: '#14b8a6',
      boxShadow: '0 4px 12px rgba(20, 184, 166, 0.2)',
    },
    '&.Mui-focused': {
      borderColor: '#14b8a6',
      boxShadow: '0 4px 16px rgba(20, 184, 166, 0.3)',
    },
  },
  '& .MuiInputBase-input': {
    padding: '14px 16px',
    fontSize: '0.95rem',
    color: '#1e293b',
    fontFamily: "'Inter', sans-serif",
  },
  '& .MuiInputLabel-root': {
    color: '#64748b',
    fontWeight: 500,
    fontSize: '0.95rem',
    fontFamily: "'Inter', sans-serif",
    '&.Mui-focused': {
      color: '#14b8a6',
    },
  },
  '& .MuiFormHelperText-root': {
    color: '#f43f5e',
    fontSize: '0.85rem',
    fontFamily: "'Inter', sans-serif",
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  borderRadius: 12,
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(8px)',
  border: '1px solid rgba(15, 23, 42, 0.1)',
  transition: 'all 0.3s ease',
  fontFamily: "'Inter', sans-serif",
  '& .MuiSelect-select': {
    padding: '16px 20px',
    fontSize: '1rem',
    color: '#1e293b',
    fontFamily: "'Inter', sans-serif",
    display: 'flex',
    alignItems: 'center',
  },
  '&:hover': {
    borderColor: '#14b8a6',
    boxShadow: '0 4px 12px rgba(20, 184, 166, 0.2)',
  },
  '&.Mui-focused': {
    borderColor: '#14b8a6',
    boxShadow: '0 4px 16px rgba(20, 184, 166, 0.3)',
  },
  height: '56px',
  width: '200px',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 4),
  fontWeight: 600,
  borderRadius: 12,
  background: 'linear-gradient(45deg, #14b8a6 30%, #0d9488 90%)',
  color: '#fff',
  textTransform: 'none',
  fontSize: '0.95rem',
  fontFamily: "'Inter', sans-serif",
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 12px rgba(20, 184, 166, 0.3)',
  '&:hover': {
    background: 'linear-gradient(45deg, #0d9488 30%, #14b8a6 90%)',
    boxShadow: '0 8px 24px rgba(20, 184, 166, 0.5)',
    transform: 'scale(1.05)',
  },
  '&:disabled': {
    background: '#e2e8f0',
    color: '#94a3b8',
    boxShadow: 'none',
  },
}));

const StyledDeleteButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 4),
  fontWeight: 600,
  borderRadius: 12,
  background: 'linear-gradient(45deg, #f43f5e 30%, #e11d48 90%)',
  color: '#fff',
  textTransform: 'none',
  fontSize: '0.95rem',
  fontFamily: "'Inter', sans-serif",
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 12px rgba(244, 63, 94, 0.3)',
  '&:hover': {
    background: 'linear-gradient(45deg, #e11d48 30%, #f43f5e 90%)',
    boxShadow: '0 8px 24px rgba(244, 63, 94, 0.5)',
    transform: 'scale(1.05)',
  },
  '&:disabled': {
    background: '#e2e8f0',
    color: '#94a3b8',
    boxShadow: 'none',
  },
}));
const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  color: '#14b8a6',
  '&.Mui-checked': {
    color: '#14b8a6',
  },
  transition: 'all 0.2s ease',
}));
const StyledSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase': {
    '& .MuiSwitch-thumb': {
      backgroundColor: '#ffffff', // Fixed: Removed stray single quote
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
    },
    '&:hover': {
      backgroundColor: 'rgba(33, 204, 187, 0.1)',
    },
    '&.Mui-checked': {
      '& .MuiSwitch-thumb': {
        backgroundColor: '#ffffff', // Fixed: Consistent syntax
      },
      '& + .MuiSwitch-track': {
        backgroundColor: '#21ccbb',
        opacity: 1,
      },
      '&:hover': {
        backgroundColor: 'rgba(33, 204, 187, 0.1)',
      },
    },
    '&.Mui-disabled': {
      opacity: 0.5,
      '& + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
  },
  '& .MuiSwitch-track': {
    backgroundColor: '#64748b',
    opacity: 1, // Adjusted to match typical MUI usage (was 0.5, but 1 is standard for tracks)
    borderRadius: 20 / 2,
  },
}));
const FileInputWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(3),
  border: '2px dashed rgba(15, 23, 42, 0.2)',
  borderRadius: 16,
  background: 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(8px)',
  minHeight: 180,
  justifyContent: 'center',
  position: 'relative',
  '&:hover': {
    borderColor: '#14b8a6',
    background: 'rgba(20, 184, 166, 0.05)',
    transform: 'scale(1.02)',
  },
  fontFamily: "'Inter', sans-serif",
}));
const ImagePreview = styled('img')({
  maxWidth: '100%',
  maxHeight: 140,
  borderRadius: 12,
  objectFit: 'contain',
  boxShadow: '0 4px 16px rgba(15, 23, 42, 0.1)',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
  },
});
const DeleteButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 12,
  right: 12,
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(8px)',
  boxShadow: '0 4px 12px rgba(15, 23, 42, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: '#fef2f2',
    transform: 'scale(1.1)',
  },
}));
const FileInfo = styled(Typography)(({ theme }) => ({
  fontSize: '0.85rem',
  color: '#64748b',
  marginTop: theme.spacing(1),
  textAlign: 'center',
  fontFamily: "'Inter', sans-serif",
}));
const RestaurantInfo = ({ restaurantId }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    designation: '',
    description: '',
    url_unique: '',
    type_boutique: '',
    pre_commande: false,
    frais_service: '',
    fidelite_service: false,
    prc_fidelite: '',
    max_jour_fidelite: '',
    date_fidelite: '',
    type_abonnement: '',
    comission_cb: '',
    comission_cash: '',
    adresse: '',
    type: '',
    lat: '',
    lng: '',
    ville_id: '',
    nom_societe: '',
    rib: '',
    email: '',
    ice: '',
    store_id: '',
    lien_google_maps: '',
    livraison: false,
    emporter: false,
    num_telephone: '',
    moyen_de_paiement: '',
    lien_facebook: '',
    lien_instagram: '',
    quiSommesNous: '',
    image: null,
    background: null,
    banner: null,
    etat: 0,
    dispatch: 'Lalivraison',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState('');
  const [backgroundPreview, setBackgroundPreview] = useState('');
  const [bannerPreview, setBannerPreview] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [disabledFields, setDisabledFields] = useState({
    comission_cb: true,
    comission_cash: true,
    store_id: true,
  });
  const fetchRestaurantData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`https://apibackoffice.lalivraison.ma/api/restaurants/${restaurantId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const restaurant = response.data;
      setFormData({
        designation: restaurant.designation || '',
        description: restaurant.description || '',
        url_unique: restaurant.url_unique || '',
        type_boutique: restaurant.type_boutique || '',
        pre_commande: !!restaurant.pre_commande,
        frais_service: restaurant.frais_service || '',
        fidelite_service: !!restaurant.fidelite_service,
        prc_fidelite: restaurant.prc_fidelite || '',
        max_jour_fidelite: restaurant.max_jour_fidelite || '',
        date_fidelite: restaurant.date_fidelite || '',
        type_abonnement: restaurant.type_abonnement === 2 ? 'Avec Livreur' : 'Sans Livreur',
        comission_cb: restaurant.comission_cb || '',
        comission_cash: restaurant.comission_cash || '',
        adresse: restaurant.adresse || '',
        type: restaurant.type || '',
        lat: restaurant.lat || '',
        lng: restaurant.lng || '',
        ville_id: restaurant.ville_id || '',
        nom_societe: restaurant.nom_societe || '',
        rib: restaurant.rib || '',
        email: restaurant.email || '',
        ice: restaurant.ice || '',
        store_id: restaurant.store_id || '',
        lien_google_maps: restaurant.lien_google_maps || '',
        livraison: !!restaurant.livraison,
        emporter: !!restaurant.emporter,
        num_telephone: restaurant.num_telephone || '',
        moyen_de_paiement: restaurant.moyen_de_paiement || '',
        lien_facebook: restaurant.lien_facebook || '',
        lien_instagram: restaurant.lien_instagram || '',
        quiSommesNous: restaurant.quiSommesNous || '',
        etat: restaurant.etat || 0,
        dispatch: restaurant.dispatch || 'Lalivraison',
      });
      setDisabledFields({
        comission_cb: restaurant.comission_cb ? true : false,
        comission_cash: restaurant.comission_cash ? true : false,
        store_id: restaurant.store_id ? true : false,
      });
      const logoBaseUrl = `https://lalivraison.ma/assets/img/images/lalivraison/${restaurant.url_unique}/articles`;
      const bannerBaseUrl = `https://lalivraison.ma/assets/logos/${restaurant.url_unique}`;
      setLogoPreview(restaurant.image ? `${logoBaseUrl}/${restaurant.image}` : '');
      setBackgroundPreview(restaurant.background ? `${logoBaseUrl}/${restaurant.background}` : '');
      setBannerPreview(restaurant.image ? `${bannerBaseUrl}/${restaurant.image}` : '');
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la récupération du restaurant.');
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);
  useEffect(() => {
    fetchRestaurantData();
  }, [fetchRestaurantData]);
  const handleChange = useCallback((e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'file' ? files[0] || null : value,
    }));
    if (type === 'file' && files[0]) {
      const previewUrl = URL.createObjectURL(files[0]);
      if (name === 'image') {
        setLogoPreview(previewUrl);
      } else if (name === 'background') {
        setBackgroundPreview(previewUrl);
      } else if (name === 'banner') {
        setBannerPreview(previewUrl);
      }
    }
  }, []);
  const handleDeleteImage = useCallback((type) => {
    if (type === 'logo') {
      setLogoPreview('');
      setFormData((prev) => ({ ...prev, image: null }));
    } else if (type === 'background') {
      setBackgroundPreview('');
      setFormData((prev) => ({ ...prev, background: null }));
    } else if (type === 'banner') {
      setBannerPreview('');
      setFormData((prev) => ({ ...prev, banner: null }));
    }
  }, []);
  const toggleFieldDisabled = (fieldName) => {
    setDisabledFields((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  };
  const handleToggleActive = async () => {
    setToggleLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await axios.patch(
        `https://apibackoffice.lalivraison.ma/api/restaurants/${restaurantId}/toggle-active`,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }
      );
      setFormData((prev) => ({
        ...prev,
        etat: response.data.etat,
        type_boutique: response.data.type_boutique,
      }));
      setSuccess('État du restaurant mis à jour avec succès !');
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de la mise à jour de l'état du restaurant.");
    } finally {
      setToggleLoading(false);
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    setError('');
    setSuccess('');
    // Validation logic: Skip required check for disabled fields with values
    if (!formData.designation) return setError('La désignation est requise.');
    if (!formData.url_unique) return setError("L'URL unique est requise.");
    if (!formData.type_abonnement) return setError("Le type d'abonnement est requis.");
    if (!formData.comission_cb && !disabledFields.comission_cb) return setError('La commission CB est requise.');
    if (!formData.comission_cash && !disabledFields.comission_cash) return setError('La commission espèces est requise.');
    if (!formData.type) return setError('Le type est requis.');
    if (!formData.ville_id) return setError("L'ID de la ville est requis.");
    if (!formData.adresse) return setError("L'adresse est requise.");
    if (!formData.num_telephone) return setError('Le numéro de téléphone est requis.');
    if (formData.type_abonnement === 'Avec Livreur' && (!formData.lat || !formData.lng)) {
      return setError('La latitude et la longitude sont requises pour "Avec Livreur".');
    }
    if (!formData.dispatch) return setError('Le dispatch est requis.');
    const sanitizedFormData = {
      ...formData,
      frais_service: formData.frais_service ? parseFloat(formData.frais_service) || 0 : 0,
      comission_cb: formData.comission_cb ? parseFloat(formData.comission_cb) || 0 : 0,
      comission_cash: formData.comission_cash ? parseFloat(formData.comission_cash) || 0 : 0,
      prc_fidelite: formData.prc_fidelite ? parseInt(formData.prc_fidelite, 10) || 0 : 0,
      max_jour_fidelite: formData.max_jour_fidelite ? parseInt(formData.max_jour_fidelite, 10) || 0 : 0,
      lat: formData.lat ? parseFloat(formData.lat) || 0 : 0,
      lng: formData.lng ? parseFloat(formData.lng) || 0 : 0,
      ville_id: formData.ville_id ? parseInt(formData.ville_id, 10) || 0 : 0,
      restaurant_id: restaurantId,
      etat: formData.etat,
      dispatch: formData.dispatch,
    };
    const data = new FormData();
    Object.entries(sanitizedFormData).forEach(([key, value]) => {
      if (value !== null && value !== '') {
        data.append(key, value);
      }
    });
    try {
      await axios.put(`https://apibackoffice.lalivraison.ma/api/restaurants/${restaurantId}`, data, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      setSuccess('Restaurant mis à jour avec succès !');
      //setTimeout(() => navigate('/restaurants'), 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la mise à jour du restaurant.');
    } finally {
      setSubmitLoading(false);
    }
  };
  const handleDelete = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce restaurant ? Cette action est irréversible.')) {
      return;
    }
    setDeleteLoading(true);
    setError('');
    setSuccess('');
    try {
      await axios.delete(`https://apibackoffice.lalivraison.ma/api/restaurants/${restaurantId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setSuccess('Restaurant supprimé avec succès !');
      setTimeout(() => navigate('/restaurants'), 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression du restaurant.');
    } finally {
      setDeleteLoading(false);
    }
  };
  const handleCloseSnackbar = () => {
    setError('');
    setSuccess('');
  };
  return (
    <>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress size={40} sx={{ color: '#14b8a6' }} />
        </Box>
      )}
      <form onSubmit={handleSubmit}>
        <Paper elevation={0} sx={{ p: { xs: 2, sm: 4 }, mb: 4, borderRadius: 16, bgcolor: 'transparent' }}>
          <SectionTitle variant="h6">Statut du restaurant</SectionTitle>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <StyledSwitch
                    checked={formData.etat === 1}
                    onChange={handleToggleActive}
                    disabled={toggleLoading || loading}
                  />
                }
                label={formData.etat === 1 ? 'Restaurant Actif' : 'Restaurant Inactif'}
                sx={{ color: '#64748b', fontFamily: "'Inter', sans-serif" }}
              />
            </Grid>
          </Grid>
          <SectionTitle variant="h6">Images</SectionTitle>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle1" gutterBottom sx={{ color: '#64748b', fontWeight: 500, fontFamily: "'Inter', sans-serif" }}>
                Logo
              </Typography>
              <FileInputWrapper>
                {logoPreview ? (
                  <>
                    <ImagePreview src={logoPreview} alt="Aperçu du logo" onError={() => setLogoPreview('')} />
                    <FileInfo>{formData.image?.name || 'logo.jpg'}</FileInfo>
                    <DeleteButton onClick={() => handleDeleteImage('logo')}>
                      <DeleteIcon sx={{ color: '#f43f5e' }} />
                    </DeleteButton>
                  </>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ color: '#64748b', fontFamily: "'Inter', sans-serif" }}>
                      Télécharger le logo (JPG, PNG)
                    </Typography>
                    <input
                      type="file"
                      name="image"
                      onChange={handleChange}
                      accept="image/jpeg,image/jpg,image/png"
                      style={{ opacity: 0, position: 'absolute', width: '100%', height: '100%', cursor: 'pointer' }}
                    />
                  </>
                )}
              </FileInputWrapper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle1" gutterBottom sx={{ color: '#64748b', fontWeight: 500, fontFamily: "'Inter', sans-serif" }}>
                Arrière-plan
              </Typography>
              <FileInputWrapper>
                {backgroundPreview ? (
                  <>
                    <ImagePreview src={backgroundPreview} alt="Aperçu de l'arrière-plan" onError={() => setBackgroundPreview('')} />
                    <FileInfo>{formData.background?.name || 'background.jpg'}</FileInfo>
                    <DeleteButton onClick={() => handleDeleteImage('background')}>
                      <DeleteIcon sx={{ color: '#f43f5e' }} />
                    </DeleteButton>
                  </>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ color: '#64748b', fontFamily: "'Inter', sans-serif" }}>
                      Télécharger l'arrière-plan (JPG, PNG)
                    </Typography>
                    <input
                      type="file"
                      name="background"
                      onChange={handleChange}
                      accept="image/jpeg,image/jpg,image/png"
                      style={{ opacity: 0, position: 'absolute', width: '100%', height: '100%', cursor: 'pointer' }}
                    />
                  </>
                )}
              </FileInputWrapper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle1" gutterBottom sx={{ color: '#64748b', fontWeight: 500, fontFamily: "'Inter', sans-serif" }}>
                Bannière Page d'Accueil
              </Typography>
              <FileInputWrapper>
                {bannerPreview ? (
                  <>
                    <ImagePreview src={bannerPreview} alt="Aperçu de la bannière" onError={() => setBannerPreview('')} />
                    <FileInfo>{formData.banner?.name || 'banner.jpg'}</FileInfo>
                    <DeleteButton onClick={() => handleDeleteImage('banner')}>
                      <DeleteIcon sx={{ color: '#f43f5e' }} />
                    </DeleteButton>
                  </>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ color: '#64748b', fontFamily: "'Inter', sans-serif" }}>
                      Télécharger la bannière (JPG, PNG)
                    </Typography>
                    <input
                      type="file"
                      name="banner"
                      onChange={handleChange}
                      accept="image/jpeg,image/jpg,image/png"
                      style={{ opacity: 0, position: 'absolute', width: '100%', height: '100%', cursor: 'pointer' }}
                    />
                  </>
                )}
              </FileInputWrapper>
            </Grid>
          </Grid>
          <SectionTitle variant="h6">Informations de base</SectionTitle>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                label="Désignation"
                name="designation"
                value={formData.designation}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                label="URL unique"
                name="url_unique"
                value={formData.url_unique}
                onChange={handleChange}
                onKeyDown={(e) => {
                  if (e.key === ' ') e.preventDefault();
                }}
                onInput={(e) => {
                  e.target.value = e.target.value.replace(/\s/g, '');
                }}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                label="Type de boutique"
                name="type_boutique"
                value={formData.type_boutique}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                label="Store ID"
                name="store_id"
                value={formData.store_id}
                onChange={handleChange}
                fullWidth
                disabled={disabledFields.store_id}
              />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                fullWidth
                multiline
                rows={4}
              />
            </Grid>
          </Grid>
          <SectionTitle variant="h6">Réseaux sociaux</SectionTitle>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                label="Lien Facebook"
                name="lien_facebook"
                value={formData.lien_facebook}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                label="Lien Instagram"
                name="lien_instagram"
                value={formData.lien_instagram}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                label="Qui sommes-nous"
                name="quiSommesNous"
                value={formData.quiSommesNous}
                onChange={handleChange}
                fullWidth
                multiline
                rows={4}
              />
            </Grid>
          </Grid>
          <SectionTitle variant="h6">Options de service</SectionTitle>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <StyledCheckbox
                    name="pre_commande"
                    checked={formData.pre_commande}
                    onChange={handleChange}
                  />
                }
                label="Pré-commande"
                sx={{ color: '#64748b', fontFamily: "'Inter', sans-serif" }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <StyledCheckbox
                    name="livraison"
                    checked={formData.livraison}
                    onChange={handleChange}
                  />
                }
                label="Livraison"
                sx={{ color: '#64748b', fontFamily: "'Inter', sans-serif" }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <StyledCheckbox
                    name="emporter"
                    checked={formData.emporter}
                    onChange={handleChange}
                  />
                }
                label="À emporter"
                sx={{ color: '#64748b', fontFamily: "'Inter', sans-serif" }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                label="Moyens de paiement"
                name="moyen_de_paiement"
                value={formData.moyen_de_paiement}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="type-abonnement-label" sx={{ fontFamily: "'Inter', sans-serif" }}>
                  Type d'abonnement
                </InputLabel>
                <StyledSelect
                  labelId="type-abonnement-label"
                  label="Type d'abonnement"
                  name="type_abonnement"
                  value={formData.type_abonnement}
                  onChange={handleChange}
                >
                  <MenuItem value="Avec Livreur">Avec Livreur</MenuItem>
                  <MenuItem value="Sans Livreur">Sans Livreur</MenuItem>
                </StyledSelect>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="dispatch-label" sx={{ fontFamily: "'Inter', sans-serif" }}>
                  Dispatch
                </InputLabel>
                <StyledSelect
                  labelId="dispatch-label"
                  label="Dispatch"
                  name="dispatch"
                  value={formData.dispatch}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="Lalivraison">Lalivraison</MenuItem>
                  <MenuItem value="Glovo">Glovo</MenuItem>
                </StyledSelect>
              </FormControl>
            </Grid>
          </Grid>
          <SectionTitle variant="h6">Informations financières</SectionTitle>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <StyledTextField
                label="Frais de service"
                name="frais_service"
                value={formData.frais_service}
                onChange={handleChange}
                fullWidth
                type="number"
                inputProps={{ step: '0.01' }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StyledTextField
                label="Commission CB"
                name="comission_cb"
                value={formData.comission_cb}
                onChange={handleChange}
                onFocus={() => !formData.comission_cb && toggleFieldDisabled('comission_cb')}
                fullWidth
                type="number"
                inputProps={{ step: '0.01' }}
                required
                disabled={disabledFields.comission_cb && !!formData.comission_cb}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StyledTextField
                label="Commission Espèces"
                name="comission_cash"
                value={formData.comission_cash}
                onChange={handleChange}
                onFocus={() => !formData.comission_cash && toggleFieldDisabled('comission_cash')}
                fullWidth
                type="number"
                inputProps={{ step: '0.01' }}
                required
                disabled={disabledFields.comission_cash && !!formData.comission_cash}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <StyledCheckbox
                    name="fidelite_service"
                    checked={formData.fidelite_service}
                    onChange={handleChange}
                  />
                }
                label="Service de fidélité"
                sx={{ color: '#64748b', fontFamily: "'Inter', sans-serif" }}
              />
            </Grid>
            {formData.fidelite_service && (
              <>
                <Grid item xs={12} sm={4}>
                  <StyledTextField
                    label="Pourcentage de fidélité"
                    name="prc_fidelite"
                    value={formData.prc_fidelite}
                    onChange={handleChange}
                    fullWidth
                    type="number"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <StyledTextField
                    label="Jours max de fidélité"
                    name="max_jour_fidelite"
                    value={formData.max_jour_fidelite}
                    onChange={handleChange}
                    fullWidth
                    type="number"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <StyledTextField
                    label="Date de fidélité"
                    name="date_fidelite"
                    value={formData.date_fidelite}
                    onChange={handleChange}
                    fullWidth
                    type="date"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}
          </Grid>
          <SectionTitle variant="h6">Informations de localisation</SectionTitle>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <StyledTextField
                label="Adresse"
                name="adresse"
                value={formData.adresse}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                label="Lien Google Maps"
                name="lien_google_maps"
                value={formData.lien_google_maps}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                label="ID de la ville"
                name="ville_id"
                value={formData.ville_id}
                onChange={handleChange}
                fullWidth
                type="number"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                label="Latitude"
                name="lat"
                value={formData.lat}
                onChange={handleChange}
                fullWidth
                type="number"
                inputProps={{ step: '0.000001' }}
                required={formData.type_abonnement === 'Avec Livreur'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                label="Longitude"
                name="lng"
                value={formData.lng}
                onChange={handleChange}
                fullWidth
                type="number"
                inputProps={{ step: '0.000001' }}
                required={formData.type_abonnement === 'Avec Livreur'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                label="Type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
          </Grid>
          <SectionTitle variant="h6">Informations sur l'entreprise</SectionTitle>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                label="Nom de la société"
                name="nom_societe"
                value={formData.nom_societe}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                label="RIB"
                name="rib"
                value={formData.rib}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                label="ICE"
                name="ice"
                value={formData.ice}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                label="Numéro de téléphone"
                name="num_telephone"
                value={formData.num_telephone}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
          </Grid>
        </Paper>
        <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <StyledButton type="submit" disabled={submitLoading || loading || toggleLoading || deleteLoading}>
            {submitLoading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Mettre à jour'}
          </StyledButton>
          <Button
            variant="outlined"
            onClick={() => navigate('/restaurants')}
            disabled={submitLoading || loading || toggleLoading || deleteLoading}
            sx={{
              px: 4,
              py: 1.5,
              borderColor: '#14b8a6',
              color: '#14b8a6',
              borderRadius: '12px',
              fontWeight: 600,
              textTransform: 'none',
              fontFamily: "'Inter', sans-serif",
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: '#0d9488',
                color: '#0d9488',
                background: 'rgba(20, 184, 166, 0.05)',
                boxShadow: '0 4px 12px rgba(20, 184, 166, 0.2)',
              },
            }}
          >
            Annuler
          </Button>
          <StyledDeleteButton
            onClick={handleDelete}
            disabled={deleteLoading || submitLoading || loading || toggleLoading}
          >
            {deleteLoading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Supprimer'}
          </StyledDeleteButton>
        </Box>
      </form>
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: '100%', borderRadius: 12, boxShadow: '0 4px 12px rgba(15, 23, 42, 0.1)', fontFamily: "'Inter', sans-serif" }}
        >
          {error}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: '100%', borderRadius: 12, boxShadow: '0 4px 12px rgba(15, 23, 42, 0.1)', fontFamily: "'Inter', sans-serif" }}
        >
          {success}
        </Alert>
      </Snackbar>
    </>
  );
};
export default memo(RestaurantInfo);